import React, { useEffect, useState, useRef } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';
import { IoIosArrowForward } from 'react-icons/io';
import { FaLock, FaUser, FaMapMarkerAlt, FaSearch, FaHeadset, FaTruck, FaShieldAlt, FaTag, FaHeart, FaGift, FaUsers, FaWallet, FaList, FaClipboardList, FaCommentDots, FaTicketAlt, FaTachometerAlt, FaGlobe } from 'react-icons/fa';
import { FaCartShopping, FaBoxOpen, FaBars, FaXmark } from 'react-icons/fa6';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  get_card_products,
  get_wishlist_products,
  reset_count
} from '../store/reducers/cardReducer';
import { push_notification } from '../store/reducers/notificationReducer';
import { getSearchSuggestions, clearSuggestions } from '../store/reducers/homeReducer';
import { user_reset, getAddresses } from '../store/reducers/authReducer';
import Logo from './Logo';
import socket from '../socket/socket';
import { useTranslation } from "react-i18next";
import api from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import Container from './Container';

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { categorys, suggestions } = useSelector((state) => state.home);
  const { userInfo, addresses = [] } = useSelector((state) => state.auth);
  const { notifications } = useSelector((state) => state.notification);
  const { card_product_count, wishlist_count } = useSelector((state) => state.card);

  const [searchValue, setSearchValue] = useState('');
  const [category, setCategory] = useState('');
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [selectedLabel, setSelectedLabel] = useState('Categories');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [mobileOpenCategory, setMobileOpenCategory] = useState(null);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  const wishlistCount = useSelector((state) => state.wishlist?.items?.length);
  const megaMenuTimer = useRef(null);

  const UAE_EMIRATES = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];
  const COUNTRIES = [
    'Afghanistan','Australia','Bahrain','Bangladesh','Canada','China','Egypt','France','Germany',
    'India','Indonesia','Iran','Iraq','Italy','Japan','Jordan','Kenya','Kuwait','Lebanon','Malaysia',
    'Morocco','Netherlands','New Zealand','Nigeria','Oman','Pakistan','Philippines','Qatar',
    'Russia','Saudi Arabia','Singapore','South Africa','South Korea','Spain','Sri Lanka',
    'Sudan','Sweden','Switzerland','Thailand','Turkey','Uganda','United Kingdom','United States',
    'Yemen','Zimbabwe'
  ];

  const [location, setLocation] = useState(localStorage.getItem('deliveryLocation') || 'Select Location');
  const [showLocationBox, setShowLocationBox] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const searchRef = useRef(null);
  const locationRef = useRef(null);
  const megaMenuTriggerRef = useRef(null);
  const megaMenuPanelRef = useRef(null);

  const { i18n } = useTranslation();
  const { currency, setCurrency, supportedCurrencies } = useCurrency();

  const getLangFromCookie = () => {
    const match = document.cookie.match(/googtrans=\/en\/(\w+)/);
    return match ? match[1] : 'en';
  };

  const [currentLang, setCurrentLang] = useState(getLangFromCookie());
  const LANG_LABELS = { en: '🇬🇧 English', hi: '🇮🇳 Hindi', ar: '🇦🇪 Arabic' };

  const changeLanguage = (lang) => {
    const host = window.location.hostname;
    const setCookie = (value) => {
      document.cookie = `googtrans=${value}; path=/`;
      if (host && host !== 'localhost') {
        document.cookie = `googtrans=${value}; path=/; domain=${host}`;
        document.cookie = `googtrans=${value}; path=/; domain=.${host}`;
      }
    };
    localStorage.setItem('siteLang', lang);
    if (lang === 'en') { setCookie('/en/en'); } else { setCookie(`/en/${lang}`); }
    window.location.reload();
  };

  const openMegaMenu = () => {
    clearTimeout(megaMenuTimer.current);
    if (!hoveredCategory && categorys.length > 0) {
      setHoveredCategory(categorys[0]);
      setCategory(categorys[0].name);
    }
    setMegaMenuOpen(true);
  };

  const closeMegaMenu = () => {
    megaMenuTimer.current = setTimeout(() => setMegaMenuOpen(false), 180);
  };

  useEffect(() => {
    const removeGoogleBanner = () => {
      document.body.style.top = '0px';
      ['iframe.goog-te-banner-frame', '.goog-te-banner-frame',
       '.VIpgJd-ZVi9od-ORHb-OEVmcd', '.VIpgJd-ZVi9od-aZ2wEe-wOHMyf']
        .forEach(s => document.querySelectorAll(s).forEach(el => el.remove()));
    };
    const observer = new MutationObserver(removeGoogleBanner);
    observer.observe(document.body, { childList: true, subtree: true });
    removeGoogleBanner();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!userInfo?.id) return;
    socket.emit('add_user', userInfo.id.toString(), userInfo);
    socket.on('notification', (data) => dispatch(push_notification(data)));
    return () => socket.off('notification');
  }, [userInfo, dispatch]);

  useEffect(() => {
    if (!searchValue.trim()) {
      dispatch(clearSuggestions());
      setShowSuggestions(false);
      return;
    }
    const timeout = setTimeout(() => {
      dispatch(getSearchSuggestions(searchValue));
      setShowSuggestions(true);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchValue, dispatch]);

  const search = () => {
    navigate(`/products/search?category=${category}&&value=${searchValue}`);
    setShowSuggestions(false);
  };

  useEffect(() => {
    if (userInfo) {
      dispatch(get_card_products(userInfo.id));
      dispatch(get_wishlist_products(userInfo.id));
      dispatch(getAddresses());
    }
  }, [userInfo, dispatch]);

  const unreadCount = notifications?.filter((n) => !n.isRead)?.length || 0;

  const handleLogout = async () => {
    await api.get('/customer/logout');
    localStorage.removeItem('customerToken');
    dispatch(user_reset());
    dispatch(reset_count());
    navigate('/login');
  };

  const selectLocation = (loc) => {
    localStorage.setItem('deliveryLocation', loc);
    setLocation(loc);
    setShowLocationBox(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target))
        setShowLocationBox(false);
      const clickedTrigger = megaMenuTriggerRef.current?.contains(e.target);
      const clickedPanel = megaMenuPanelRef.current?.contains(e.target);
      if (!clickedTrigger && !clickedPanel)
        setMegaMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="w-full">

      {/* ===== PROMO TOP BAR ===== */}
      <div className="bg-[#1a1a1a] text-white md-lg:hidden">
        <Container className="flex items-center justify-between h-[58px] text-[15px]">
          <Link to="/products?freeShipping=true" className="flex items-center gap-3 hover:text-green-400 transition-colors duration-150">
            <FaTruck className="text-green-400 flex-shrink-0 text-[18px]" />
            <span><strong>Free shipping</strong> &gt; Special for you</span>
          </Link>
          <div className="w-px h-6 bg-white/20 flex-shrink-0" />
          <Link to="/faq" className="flex items-center gap-3 hover:text-yellow-400 transition-colors duration-150">
            <FaTag className="text-yellow-400 flex-shrink-0 text-[18px]" />
            <span><strong>Customizations</strong> Available</span>
          </Link>
          <div className="w-px h-6 bg-white/20 flex-shrink-0" />
          <Link to="/faq" className="flex items-center gap-3 hover:text-blue-400 transition-colors duration-150">
            <FaShieldAlt className="text-blue-400 flex-shrink-0 text-[18px]" />
            <span><strong>Delivery guarantee</strong> — Refund for any issues</span>
          </Link>
          <div className="w-px h-6 bg-white/20 flex-shrink-0" />
          <Link to="/wholesale-products" className="flex items-center gap-3 hover:text-gray-300 transition-colors duration-150">
            <FaBoxOpen className="text-gray-300 flex-shrink-0 text-[18px]" />
            <span><strong>Wholesale Pricing</strong> for Businesses &amp; Resellers</span>
          </Link>
        </Container>
      </div>

      {/* ===== MAIN NAV ===== */}
      {/* onMouseLeave covers the entire red navbar + the absolutely-positioned mega menu below it */}
      <div style={{ backgroundColor: '#E21C1B' }} className="relative" onMouseLeave={closeMegaMenu}>
        <Container className="flex items-center gap-2 h-[82px]">

          {/* LOGO */}
          <Link to="/" className="flex-shrink-0 mr-4 group">
            <Logo className="transition-transform duration-200 group-hover:scale-[1.04]" />
          </Link>

          {/* QUICK NAV — desktop only */}
          <div className="flex items-center text-white text-[15px] font-medium md-lg:hidden flex-shrink-0 gap-0.5">

            {/* DELIVERY LOCATION */}
            <div
              ref={locationRef}
              className="relative"
              onMouseEnter={() => { clearTimeout(megaMenuTimer.current); setMegaMenuOpen(false); }}
            >
              <div
                onClick={() => { clearTimeout(megaMenuTimer.current); setMegaMenuOpen(false); setShowLocationBox(!showLocationBox); }}
                className="group/location flex items-center gap-1.5 px-2.5 py-1.5 rounded hover:bg-white/20 cursor-pointer whitespace-nowrap transition"
              >
                <FaMapMarkerAlt className="text-yellow-300 text-lg" />
                <span className="hidden group-hover/location:inline text-yellow-300 font-semibold max-w-[90px] truncate">{location}</span>
                <IoMdArrowDropdown />
              </div>
              {showLocationBox && (
                <div
                  className="absolute left-0 top-full mt-2 bg-white text-black rounded-xl shadow-2xl w-[300px] z-[9999] p-4 border border-gray-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="font-bold text-slate-800 text-sm mb-3">Choose delivery location</h3>
                  {userInfo && addresses.length > 0 && (
                    <div className="mb-3 max-h-[120px] overflow-y-auto flex flex-col gap-2">
                      {addresses.map((addr, i) => {
                        const label = [addr.area, addr.city, addr.province].filter(Boolean).join(', ');
                        const key = addr.city || addr.province || addr.area;
                        const active = location === key;
                        return (
                          <div key={i} onClick={() => selectLocation(key)}
                            className={`flex items-start gap-3 border rounded-lg p-2 cursor-pointer transition ${active ? 'border-[#059473] bg-green-50' : 'hover:border-gray-400'}`}>
                            <div className={`mt-1 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${active ? 'border-[#059473] bg-[#059473]' : 'border-gray-300'}`} />
                            <div>
                              <p className="text-xs font-semibold text-slate-700">{addr.name}</p>
                              <p className="text-xs text-gray-500">{label}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">🇦🇪 UAE Emirates</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {UAE_EMIRATES.map((emirate) => (
                      <button key={emirate} onClick={() => selectLocation(emirate)}
                        className={`text-xs px-2 py-1 rounded-full border transition ${location === emirate ? 'bg-[#059473] text-white border-[#059473]' : 'border-gray-300 hover:border-[#059473] hover:text-[#059473]'}`}>
                        {emirate}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide mb-1">🌍 Ship outside UAE</p>
                  <input value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)}
                    placeholder="Search country..." className="border w-full px-3 py-1.5 text-sm rounded-lg outline-none focus:border-[#059473] mb-1" />
                  {countrySearch && (
                    <div className="max-h-[120px] overflow-y-auto rounded-lg border">
                      {COUNTRIES.filter((c) => c.toLowerCase().includes(countrySearch.toLowerCase())).map((country) => (
                        <div key={country} onClick={() => { selectLocation(country); setCountrySearch(''); }}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center gap-2 ${location === country ? 'text-[#059473] font-semibold' : ''}`}>
                          {location === country && <FaMapMarkerAlt className="text-[#059473] text-xs flex-shrink-0" />}
                          {country}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── CATEGORIES MEGA MENU TRIGGER ── */}
            <div ref={megaMenuTriggerRef} onMouseEnter={openMegaMenu} className="relative">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer whitespace-nowrap transition-all duration-150 ${megaMenuOpen ? 'bg-white/25' : 'hover:bg-white/20'}`}>
                <span>{selectedLabel}</span>
                <IoMdArrowDropdown className={`transition-transform duration-200 ${megaMenuOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </div>

          {/* SEARCH BAR */}
          <div className="flex-[3] min-w-0 md-lg:flex-1 sm:hidden relative" ref={searchRef}>
            <div className="flex bg-white rounded-full overflow-hidden shadow-sm h-[50px]">
              <input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onFocus={() => searchValue && setShowSuggestions(true)}
                onKeyDown={(e) => e.key === 'Enter' && search()}
                placeholder="Search products..."
                className="flex-grow px-5 outline-none text-[15px] text-gray-700"
              />
              <button
                onClick={search}
                className="w-[52px] flex-shrink-0 bg-[#1a1a1a] text-white flex items-center justify-center hover:bg-black transition rounded-r-full"
              >
                <FaSearch className="text-sm" />
              </button>
            </div>

            {showSuggestions && suggestions?.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border shadow-lg rounded-xl mt-1 z-[9999] max-h-[280px] overflow-y-auto">
                {suggestions.map((s, i) => (
                  <div
                    key={i}
                    onClick={() => { setSearchValue(s.name); setShowSuggestions(false); search(); }}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 border-b border-gray-50 last:border-0"
                  >
                    <FaSearch className="text-gray-400 text-xs flex-shrink-0" />
                    {s.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* SELLER LOGIN */}
          <Link
            to="/become-seller"
            className="flex flex-col items-center gap-0.5 text-white hover:bg-white/15 px-2.5 py-1.5 rounded transition flex-shrink-0 md-lg:hidden"
          >
            <FaLock className="text-[21px]" />
            <span className="text-[13px] whitespace-nowrap">Seller Login</span>
          </Link>

          {/* USER ACCOUNT */}
          <div className="relative group flex-shrink-0 sm:ml-auto">
            {userInfo ? (
              <div className="flex items-center gap-2 cursor-pointer text-white hover:bg-white/15 px-2 py-1.5 rounded transition">
                <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                  <FaUser className="text-base" />
                </div>
                <div className="md-lg:hidden leading-tight">
                  <div className="text-[13px] opacity-80">Hello, {userInfo.name.split(' ')[0]}</div>
                  <div className="text-[14px] font-bold">Orders &amp; Account</div>
                </div>
                <IoMdArrowDropdown className="md-lg:hidden opacity-70" />
              </div>
            ) : (
              <Link
                to="/customer-auth"
                className="flex items-center gap-2 text-white hover:bg-white/15 px-2 py-1.5 rounded transition"
              >
                <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                  <FaUser className="text-base" />
                </div>
                <div className="md-lg:hidden leading-tight">
                  <div className="text-[13px] opacity-80">Hello, sign in</div>
                  <div className="text-[14px] font-bold">Orders &amp; Account</div>
                </div>
              </Link>
            )}

            {userInfo && (
              <>
                <div className="absolute left-0 right-0 h-3 top-full" />
                <div className="absolute right-0 top-full mt-2 w-[260px] bg-white rounded-xl shadow-xl hidden group-hover:block z-[9999] text-sm overflow-hidden border border-gray-100">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <p className="font-semibold text-gray-800">{userInfo.name}</p>
                    <p className="text-xs text-gray-500">{userInfo.email}</p>
                  </div>
                  <ul className="py-1 text-gray-700">
                    <li>
                      <Link to="/dashboard" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaTachometerAlt className="text-gray-400 text-sm flex-shrink-0" />
                        <span>Overview</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/notifications" className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <div className="flex items-center gap-3">
                          <FaCommentDots className="text-gray-400 text-sm flex-shrink-0" />
                          <span>Notifications</span>
                        </div>
                        {unreadCount > 0 && (
                          <span className="text-xs font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                        )}
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/my-orders" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaList className="text-gray-400 text-sm flex-shrink-0" />
                        <span>My Orders</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/my-wishlist" className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <div className="flex items-center gap-3">
                          <FaHeart className="text-gray-400 text-sm flex-shrink-0" />
                          <span>Wishlist</span>
                        </div>
                        <span className="text-xs text-gray-400">({wishlist_count || 0})</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/my-list" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaClipboardList className="text-gray-400 text-sm flex-shrink-0" />
                        <span>My List</span>
                      </Link>
                    </li>
                    <li className="border-t border-gray-100 mt-1 pt-1">
                      <Link to="/dashboard/my-profile" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaUser className="text-gray-400 text-sm flex-shrink-0" />
                        <span>My Profile</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/my-coupons" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaTicketAlt className="text-gray-400 text-sm flex-shrink-0" />
                        <span>Coupons</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/my-rewards" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaGift className="text-gray-400 text-sm flex-shrink-0" />
                        <span>Rewards</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/my-referrals" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaUsers className="text-gray-400 text-sm flex-shrink-0" />
                        <span>Referrals</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/credit-balance" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaWallet className="text-gray-400 text-sm flex-shrink-0" />
                        <span>Credit Balance</span>
                      </Link>
                    </li>
                    <li>
                      <Link to="/dashboard/chat" className="flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 hover:text-orange-600 transition">
                        <FaHeadset className="text-gray-400 text-sm flex-shrink-0" />
                        <span>Chat with Support</span>
                      </Link>
                    </li>
                    <li className="border-t border-gray-100 mt-1">
                      <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-red-500 hover:bg-red-50 transition">
                        <FaLock className="text-sm flex-shrink-0" />
                        <span>Logout</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>

          {/* SUPPORT */}
          <Link
            to="/faq"
            className="flex flex-col items-center gap-0.5 text-white hover:bg-white/15 px-2.5 py-1.5 rounded transition flex-shrink-0 md-lg:hidden"
          >
            <FaHeadset className="text-[21px]" />
            <span className="text-[13px]">Support</span>
          </Link>

          {/* WHOLESALE */}
          <div className="relative group flex-shrink-0 md-lg:hidden">
            <div className="flex flex-col items-center gap-0.5 cursor-pointer text-white hover:bg-white/15 px-2.5 py-1.5 rounded transition">
              <FaBoxOpen className="text-[21px]" />
              <span className="text-[13px]">Wholesale</span>
            </div>
            <div className="absolute left-0 right-0 h-3 top-full" />
            <ul className="absolute right-0 top-full mt-2 hidden group-hover:block bg-white border border-gray-100 shadow-xl rounded-xl w-[180px] z-50 text-sm text-black overflow-hidden">
              <li onClick={() => navigate('/bulk-products')} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b">Bulk Products</li>
              <li onClick={() => navigate('/wholesale-products')} className="px-4 py-3 hover:bg-gray-50 cursor-pointer">Wholesale Products</li>
            </ul>
          </div>

          {/* LANGUAGE + CURRENCY */}
          <div className="relative group text-white cursor-pointer flex-shrink-0 hover:bg-white/15 px-2.5 py-1.5 rounded transition md-lg:hidden">
            <div className="flex flex-col items-center gap-0.5">
              <FaGlobe className="text-[19px]" />
              <span className="text-[13px]">{LANG_LABELS[currentLang]?.split(' ')[1] || 'English'}</span>
            </div>
            <div className="absolute left-0 right-0 h-3 top-full" />
            <ul className="absolute right-0 top-full mt-2 hidden group-hover:block bg-white border border-gray-100 shadow-xl rounded-xl w-[200px] z-50 text-sm text-black overflow-hidden">
              <li className="px-4 py-2 text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50">Language</li>
              {[['en', '🇬🇧 English'], ['hi', '🇮🇳 Hindi'], ['ar', '🇦🇪 Arabic']].map(([code, label]) => (
                <li key={code} onClick={() => changeLanguage(code)}
                  className={`px-4 py-2.5 hover:bg-gray-50 cursor-pointer flex items-center justify-between ${currentLang === code ? 'font-semibold text-[#059473]' : ''}`}>
                  {label}
                  {currentLang === code && <span className="text-[#059473] font-bold">✓</span>}
                </li>
              ))}
              <li className="px-4 py-2 text-[11px] uppercase tracking-wide text-gray-400 bg-gray-50 border-t">Currency</li>
              {supportedCurrencies.map((code) => (
                <li key={code} onClick={() => setCurrency(code)}
                  className={`px-4 py-2.5 hover:bg-gray-50 cursor-pointer flex items-center justify-between ${currency === code ? 'font-semibold text-[#059473]' : ''}`}>
                  {code}
                  {currency === code && <span className="text-[#059473] font-bold">✓</span>}
                </li>
              ))}
            </ul>
          </div>

          {/* CART */}
          <div
            onClick={() => navigate('/card')}
            className="relative cursor-pointer flex items-center gap-2 bg-white text-[#E21C1B] hover:bg-yellow-50 hover:scale-[1.04] px-3.5 py-2 rounded-full shadow-md transition flex-shrink-0 ml-1"
          >
            <FaCartShopping className="text-[22px]" />
            <span className="text-[14px] font-bold md-lg:hidden">Cart</span>
            {card_product_count !== 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-yellow-400 text-black text-[11px] w-[21px] h-[21px] rounded-full flex justify-center items-center font-bold leading-none ring-2 ring-white">
                {card_product_count}
              </span>
            )}
          </div>

          {/* HAMBURGER — mobile */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="hidden md-lg:flex text-white text-xl flex-shrink-0 items-center justify-center p-1"
          >
            {menuOpen ? <FaXmark /> : <FaBars />}
          </button>

        </Container>

        {/* PHONE SEARCH — the inline bar has no room at this width */}
        <div className="hidden sm:block px-4 pb-3 -mt-2">
          <div className="flex bg-white rounded-full overflow-hidden shadow-sm h-[42px]">
            <input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="Search products..."
              className="flex-grow min-w-0 px-4 outline-none text-[15px] text-gray-700"
            />
            <button onClick={search} aria-label="Search" className="w-[46px] flex-shrink-0 bg-[#1a1a1a] text-white flex items-center justify-center rounded-r-full">
              <FaSearch className="text-sm" />
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            PREMIUM MEGA MENU PANEL
            Positioned absolute below the red navbar.
            It's a DOM child of the red div so mouse
            events propagate correctly — onMouseLeave
            on the parent only fires when truly leaving.
        ════════════════════════════════════════════ */}
        {megaMenuOpen && (
          <div ref={megaMenuPanelRef} className="absolute top-full left-0 right-0 z-[9997] md-lg:hidden mega-menu-enter">
            <div className="max-w-[1480px] mx-auto px-6 flex justify-start">
              <div className="bg-white border border-t-0 border-gray-200 shadow-[0_16px_48px_rgba(0,0,0,0.15)] rounded-b-2xl flex overflow-hidden w-[980px] max-w-[calc(100vw-48px)]" style={{ height: 560 }}>

                {/* ── LEFT SIDEBAR ── */}
                <div className="w-[220px] flex-shrink-0 border-r border-gray-100 py-3 overflow-y-auto">
                  {categorys.map((cat, i) => {
                    const isActive = hoveredCategory?.name === cat.name;
                    return (
                      <Link
                        key={i}
                        to={`/products?category=${cat.name}`}
                        onMouseEnter={() => { setHoveredCategory(cat); setCategory(cat.name); }}
                        onClick={() => { setSelectedLabel(cat.name); setMegaMenuOpen(false); }}
                        className={`group/item flex items-center justify-between px-5 py-[11px] text-[13.5px] transition-all duration-100 ${
                          isActive
                            ? 'bg-gray-100 text-gray-900 font-semibold'
                            : 'text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <span className="truncate leading-snug">{cat.name}</span>
                        <IoIosArrowForward className={`text-[11px] flex-shrink-0 ml-1 transition-colors ${isActive ? 'text-gray-700' : 'text-gray-300 group-hover/item:text-gray-500'}`} />
                      </Link>
                    );
                  })}
                </div>

                {/* ── RIGHT PANEL ── */}
                <div className="flex-1 py-6 px-8 overflow-y-auto">
                  {hoveredCategory ? (
                    hoveredCategory.subcategories?.length > 0 ? (
                      <div className="grid grid-cols-5 gap-x-6 gap-y-8">
                        {hoveredCategory.subcategories.map((sub, idx) => (
                          <Link
                            key={idx}
                            to={`/products?category=${hoveredCategory.name}&subCategory=${sub.name}`}
                            onClick={() => setMegaMenuOpen(false)}
                            className="group/sub flex flex-col items-center gap-2.5 text-center"
                          >
                            <div className="relative w-[78px] h-[78px] flex-shrink-0 mx-auto">
                              <div className="w-full h-full rounded-full overflow-hidden border-[3px] border-gray-100 group-hover/sub:border-[#E21C1B] transition-all duration-200 bg-gray-50 shadow-sm group-hover/sub:shadow-md">
                                <img
                                  src={sub.image}
                                  alt={sub.name}
                                  className="w-full h-full object-cover group-hover/sub:scale-[1.08] transition-transform duration-300"
                                />
                              </div>
                              {idx < 3 && (
                                <span className="absolute -top-1 -right-1 bg-[#E21C1B] text-white text-[8px] font-bold px-[5px] py-[2px] rounded-full leading-none tracking-wide uppercase">
                                  HOT
                                </span>
                              )}
                            </div>
                            <p className="text-[12px] text-gray-700 group-hover/sub:text-[#E21C1B] font-medium leading-tight line-clamp-2 max-w-[92px] transition-colors duration-150 w-full">
                              {sub.name}
                            </p>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <p className="text-sm font-medium">No subcategories listed</p>
                        <Link
                          to={`/products?category=${hoveredCategory.name}`}
                          onClick={() => setMegaMenuOpen(false)}
                          className="mt-3 text-sm text-[#E21C1B] font-semibold hover:underline"
                        >
                          Browse all {hoveredCategory.name}
                        </Link>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-300 text-sm select-none">
                      Hover a category to explore
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        )}

      </div>

      {/* ═══════════════════════════════════════════
          MOBILE MENU DRAWER
      ════════════════════════════════════════════ */}
      {menuOpen && (
        <div className="hidden md-lg:block bg-[#E21C1B] border-t border-white/20 text-white text-sm">

          {/* Mobile Search (phones already get the search row above) */}
          <div className="px-4 pt-4 pb-3 border-b border-white/10 sm:hidden">
            <div className="flex bg-white rounded-full overflow-hidden h-[40px]">
              <input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && search()}
                placeholder="Search products..."
                className="flex-grow px-4 outline-none text-sm text-gray-700"
              />
              <button onClick={search} className="w-[44px] bg-[#1a1a1a] text-white flex items-center justify-center rounded-r-full">
                <FaSearch className="text-sm" />
              </button>
            </div>
          </div>

          {/* Deliver To */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="flex items-center gap-1.5 text-white/70 font-semibold uppercase tracking-wide mb-2">
              <FaMapMarkerAlt className="text-yellow-300 text-lg" /> <span className="text-white font-bold normal-case text-[13px]">{location}</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {UAE_EMIRATES.map(e => (
                <button key={e} onClick={() => { selectLocation(e); setMenuOpen(false); }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${location === e ? 'bg-white text-[#E21C1B] border-white font-semibold' : 'border-white/40 text-white/80 hover:border-white'}`}>
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Categories Accordion */}
          <div className="border-b border-white/10">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-white font-semibold text-[13px] hover:bg-white/10 transition"
            >
              <span>Browse Categories</span>
              <IoMdArrowDropdown className={`text-xl transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`} />
            </button>

            {mobileCatOpen && (
              <div className="bg-black/15 px-2 pt-1 pb-2">
                {categorys.map((cat, i) => (
                  <div key={i} className="mb-0.5">
                    <div className="flex items-center rounded-lg overflow-hidden">
                      <Link
                        to={`/products?category=${cat.name}`}
                        onClick={() => setMenuOpen(false)}
                        className="flex-1 px-3 py-2.5 text-white/90 text-[13px] font-medium hover:bg-white/10 transition rounded-l-lg"
                      >
                        {cat.name}
                      </Link>
                      {cat.subcategories?.length > 0 && (
                        <button
                          onClick={() => setMobileOpenCategory(mobileOpenCategory === cat.name ? null : cat.name)}
                          className="px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/10 transition rounded-r-lg"
                        >
                          <IoMdArrowDropdown className={`text-lg transition-transform duration-200 ${mobileOpenCategory === cat.name ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </div>

                    {mobileOpenCategory === cat.name && cat.subcategories?.length > 0 && (
                      <div className="pl-3 pr-1 pt-1 pb-2 grid grid-cols-2 gap-1">
                        {cat.subcategories.map((sub, j) => (
                          <Link
                            key={j}
                            to={`/products?category=${cat.name}&subCategory=${sub.name}`}
                            onClick={() => setMenuOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition"
                          >
                            <img src={sub.image} alt={sub.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0 border border-white/20" />
                            <span className="text-white/80 text-[11.5px] leading-tight line-clamp-2">{sub.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Language + Currency row */}
          <div className="px-4 py-3 border-b border-white/10 flex gap-8 flex-wrap">
            <div>
              <p className="flex items-center gap-1 text-[11px] text-white/70 font-semibold uppercase tracking-wide mb-2">
                <FaGlobe /> Language
              </p>
              <div className="flex gap-2">
                {[['en', '🇬🇧 EN'], ['hi', '🇮🇳 HI'], ['ar', '🇦🇪 AR']].map(([code, label]) => (
                  <button key={code} onClick={() => changeLanguage(code)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${currentLang === code ? 'bg-white text-[#E21C1B] border-white font-semibold' : 'border-white/40 text-white/80 hover:border-white'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[11px] text-white/70 font-semibold uppercase tracking-wide mb-2">Currency</p>
              <div className="flex gap-2 flex-wrap">
                {supportedCurrencies.map((code) => (
                  <button key={code} onClick={() => setCurrency(code)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${currency === code ? 'bg-white text-[#E21C1B] border-white font-semibold' : 'border-white/40 text-white/80 hover:border-white'}`}>
                    {code}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Nav Links */}
          <div className="px-4 py-3 flex flex-col gap-2.5">
            {[
              ['/products?sort=best-selling', '⭐ Best-Selling Items'],
              ['/bulk-products', '🏭 Bulk Products'],
              ['/wholesale-products', '🛒 Wholesale Products'],
              ['/faq', '💬 Support / FAQ'],
            ].map(([to, label]) => (
              <Link key={to} to={to} onClick={() => setMenuOpen(false)} className="text-white/80 hover:text-white transition py-0.5">
                {label}
              </Link>
            ))}
          </div>

        </div>
      )}

    </div>
  );
};

export default Header;
